import { useEffect, useState, useCallback } from 'react';
import {
  Page,
  Header,
  Content,
  InfoCard,
  Table,
  TableColumn,
  Link,
  Breadcrumbs,
} from '@backstage/core-components';
import {
  useApi,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Grid,
  Typography,
  Chip,
  List,
  ListItem,
  ListItemText,
  makeStyles,
  Divider,
  Box,
  Tooltip,
} from '@material-ui/core';
import WarningIcon from '@material-ui/icons/Warning';

// ---------------------------------------------------------------------------
// Types (mirror backend response)
// ---------------------------------------------------------------------------

interface VaultPolicyPath {
  path: string;
  capabilities: string[];
}

interface TerraformResource {
  type: string;
  name: string;
  file: string;
}

interface TerraformInfo {
  workspaces: string[];
  resources: TerraformResource[];
}

interface DeployInfo {
  namespace: string;
  team?: string;
  slack?: string;
  opsgenie?: string;
  accounting?: string;
  product?: string;
  customer?: string;
  images: { repo: string; reference: string }[];
  replicas?: number;
  regions?: string;
  clusters?: string;
  expose?: {
    enabled: boolean;
    external?: boolean;
    hostnames?: string[];
  };
  metrics?: {
    enabled: boolean;
    path?: string;
    port?: string;
  };
  access?: { service: string; namespace: string; permissions: string[] }[];
  instances?: string[];
}

interface K8sInfo {
  strategy?: string;
  resources?: { cpu?: string; memory?: string };
  ports?: { containerPort: number; name: string }[];
  readinessProbe?: {
    path?: string;
    port?: number;
    initialDelaySeconds?: number;
    periodSeconds?: number;
  };
  annotations?: Record<string, string>;
}

interface EnvironmentInfo {
  name: string;
  deploy?: DeployInfo;
  k8s?: K8sInfo;
}

interface ArgoEnvironmentDetail {
  name: string;
  cluster: string;
  url: string;
  branch?: string;
}

interface ArgoCDInfo {
  namespace?: string;
  repoURL?: string;
  sourcePath?: string;
  project?: string;
  environments: ArgoEnvironmentDetail[];
}

type SourceType = 'Service' | 'ArgoCD Application';

interface ServiceDetails {
  name: string;
  sourceType: SourceType;
  team?: string;
  vault?: VaultPolicyPath[];
  terraform?: TerraformInfo;
  environments: EnvironmentInfo[];
  argocd?: ArgoCDInfo;
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles(theme => ({
  breadcrumb: {
    marginBottom: theme.spacing(2),
  },
  mono: {
    fontFamily: 'monospace',
    fontSize: '0.85em',
  },
  metaRow: {
    display: 'flex',
    gap: theme.spacing(2),
    flexWrap: 'wrap' as const,
    marginBottom: theme.spacing(1),
  },
  metaLabel: {
    fontWeight: 600,
    marginRight: 4,
    color: theme.palette.text.secondary,
  },
  envChip: {
    margin: theme.spacing(0.5),
    color: '#fff',
    fontWeight: 500,
  },
  chip: {
    margin: theme.spacing(0.5),
  },
  pathList: {
    maxHeight: 250,
    overflow: 'auto',
    '& .MuiListItem-root': {
      paddingTop: 1,
      paddingBottom: 1,
    },
  },
  noData: {
    padding: theme.spacing(4),
    textAlign: 'center' as const,
    color: theme.palette.text.secondary,
  },
  warnChip: {
    margin: theme.spacing(0.5),
    backgroundColor: 'transparent',
    border: '1px solid #ffc107',
    color: '#e6a700',
    fontWeight: 500,
  },
  okChip: {
    margin: theme.spacing(0.5),
    fontWeight: 500,
  },
  warnIcon: {
    color: '#e6a700',
    fontSize: '1rem',
    marginRight: 4,
    verticalAlign: 'middle',
  },
}));

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const VaultAccessCard = ({ paths }: { paths: VaultPolicyPath[] }) => {
  const classes = useStyles();
  const columns: TableColumn<VaultPolicyPath>[] = [
    {
      title: 'Path',
      field: 'path',
      render: row => <span className={classes.mono}>{row.path}</span>,
    },
    {
      title: 'Capabilities',
      field: 'capabilities',
      render: row => row.capabilities.join(', '),
    },
  ];

  return (
    <InfoCard title="Vault Access" subheader="From vault-policy.hcl">
      <Table
        data={paths}
        columns={columns}
        options={{
          search: false,
          paging: paths.length > 10,
          pageSize: 10,
          padding: 'dense',
          toolbar: false,
        }}
      />
    </InfoCard>
  );
};

const TerraformCard = ({ terraform }: { terraform: TerraformInfo }) => {
  const classes = useStyles();
  const columns: TableColumn<TerraformResource>[] = [
    { title: 'Type', field: 'type', render: row => <span className={classes.mono}>{row.type}</span> },
    { title: 'Name', field: 'name', render: row => <span className={classes.mono}>{row.name}</span> },
    { title: 'File', field: 'file', render: row => <span className={classes.mono}>{row.file}</span> },
  ];

  return (
    <InfoCard
      title="Terraform Resources"
      subheader={`Workspaces: ${terraform.workspaces.join(', ')}`}
    >
      {terraform.resources.length > 0 ? (
        <Table
          data={terraform.resources}
          columns={columns}
          options={{
            search: false,
            paging: terraform.resources.length > 10,
            pageSize: 10,
            padding: 'dense',
            toolbar: false,
          }}
        />
      ) : (
        <Typography variant="body2" color="textSecondary">
          No resources defined (configuration only).
        </Typography>
      )}
    </InfoCard>
  );
};

const ENV_COLORS: Record<string, string> = {
  prod: '#d32f2f',
  production: '#d32f2f',
  stg: '#f57c00',
  staging: '#f57c00',
  dev: '#388e3c',
  ci: '#1976d2',
  uat: '#7b1fa2',
};

function envColor(name: string): string {
  const lower = name.toLowerCase();
  for (const [key, color] of Object.entries(ENV_COLORS)) {
    if (lower.startsWith(key)) return color;
  }
  return '#607d8b';
}

const MetaItem = ({ label, value }: { label: string; value?: string }) => {
  if (!value) return null;
  const classes = useStyles();
  return (
    <span>
      <span className={classes.metaLabel}>{label}:</span>
      {value}
    </span>
  );
};

const OUTDATED_INSTANCE_RE = /^[rmc][456][ia]/;
const INSTANCE_UPGRADE_TIP =
  'Consider upgrading to a gen-7 or gen-8 instance (e.g. c7i, m8a, r7i) or ask InfraPlat about moving to Karpenter.';

const EnvironmentCard = ({ env }: { env: EnvironmentInfo }) => {
  const classes = useStyles();
  const { deploy, k8s } = env;

  return (
    <InfoCard
      title={
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <Chip
            label={env.name}
            size="small"
            className={classes.envChip}
            style={{ backgroundColor: envColor(env.name) }}
          />
          <Typography variant="h6">Environment</Typography>
        </Box>
      }
    >
      {deploy && (
        <>
          <Box className={classes.metaRow}>
            <MetaItem label="Team" value={deploy.team} />
            <MetaItem label="Slack" value={deploy.slack} />
            <MetaItem label="OpsGenie" value={deploy.opsgenie} />
            <MetaItem label="Accounting" value={deploy.accounting} />
            <MetaItem label="Product" value={deploy.product} />
            <MetaItem label="Customer" value={deploy.customer} />
          </Box>

          {deploy.images.length > 0 && (
            <Box className={classes.metaRow}>
              {deploy.images.map((img, i) => (
                <span key={i}>
                  <span className={classes.metaLabel}>Image:</span>
                  <span className={classes.mono}>{img.repo}</span>
                  {' @ '}
                  <span className={classes.mono}>{img.reference}</span>
                </span>
              ))}
            </Box>
          )}

          <Box className={classes.metaRow}>
            {deploy.replicas !== undefined && (
              <MetaItem label="Replicas" value={String(deploy.replicas)} />
            )}
            <MetaItem label="Regions" value={deploy.regions} />
            <MetaItem label="Clusters" value={deploy.clusters} />
          </Box>

          {deploy.instances && deploy.instances.length > 0 && (
            <Box className={classes.metaRow}>
              <span className={classes.metaLabel}>Instances:</span>
              {deploy.instances.map(inst => {
                const outdated = OUTDATED_INSTANCE_RE.test(inst);
                return outdated ? (
                  <Tooltip key={inst} title={INSTANCE_UPGRADE_TIP} arrow>
                    <Chip
                      icon={<WarningIcon className={classes.warnIcon} />}
                      label={inst}
                      size="small"
                      className={classes.warnChip}
                    />
                  </Tooltip>
                ) : (
                  <Chip
                    key={inst}
                    label={inst}
                    size="small"
                    variant="outlined"
                    className={classes.okChip}
                  />
                );
              })}
            </Box>
          )}

          {deploy.expose?.enabled && (
            <Box className={classes.metaRow}>
              <span className={classes.metaLabel}>Exposed:</span>
              {deploy.expose.hostnames?.map((h, i) => {
                const label = typeof h === 'string' ? h : (h as any).hostname ?? JSON.stringify(h);
                return (
                  <Chip
                    key={label ?? i}
                    label={label}
                    size="small"
                    variant="outlined"
                    className={classes.chip}
                  />
                );
              }) ?? 'Yes (internal)'}
            </Box>
          )}

          {deploy.metrics?.enabled && (
            <Box className={classes.metaRow}>
              <MetaItem label="Metrics" value={`${deploy.metrics.path ?? '/metrics'} (port: ${deploy.metrics.port ?? 'default'})`} />
            </Box>
          )}

          {deploy.access && deploy.access.length > 0 && (
            <>
              <Typography variant="subtitle2" style={{ marginTop: 8 }}>
                Service Access
              </Typography>
              <List dense className={classes.pathList}>
                {deploy.access.map((a, i) => (
                  <ListItem key={i}>
                    <ListItemText
                      primary={
                        <span>
                          <span className={classes.mono}>
                            {a.service}/{a.namespace}
                          </span>
                          {' — '}
                          {a.permissions.join(', ')}
                        </span>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </>
      )}

      {k8s && (
        <>
          {deploy && <Divider style={{ margin: '12px 0' }} />}
          <Typography variant="subtitle2" gutterBottom>
            Kubernetes
          </Typography>
          <Box className={classes.metaRow}>
            {k8s.strategy && (
              <MetaItem label="Strategy" value={k8s.strategy} />
            )}
            {k8s.resources && (
              <>
                <MetaItem label="CPU" value={k8s.resources.cpu} />
                <MetaItem label="Memory" value={k8s.resources.memory} />
              </>
            )}
          </Box>
          {k8s.ports && k8s.ports.length > 0 && (
            <Box className={classes.metaRow}>
              <span className={classes.metaLabel}>Ports:</span>
              {k8s.ports.map(p => (
                <Chip
                  key={p.name}
                  label={`${p.name}: ${p.containerPort}`}
                  size="small"
                  variant="outlined"
                  className={classes.chip}
                />
              ))}
            </Box>
          )}
          {k8s.readinessProbe && (
            <Box className={classes.metaRow}>
              <MetaItem
                label="Readiness"
                value={`${k8s.readinessProbe.path ?? '?'}:${k8s.readinessProbe.port ?? '?'} (delay: ${k8s.readinessProbe.initialDelaySeconds ?? '?'}s, period: ${k8s.readinessProbe.periodSeconds ?? '?'}s)`}
              />
            </Box>
          )}
        </>
      )}

      {!deploy && !k8s && (
        <Typography variant="body2" color="textSecondary">
          No deploy.yaml or k8.yaml found.
        </Typography>
      )}
    </InfoCard>
  );
};

// ---------------------------------------------------------------------------
// ArgoCD sub-components
// ---------------------------------------------------------------------------

const ArgoOverviewCard = ({ argocd, team }: { argocd: ArgoCDInfo; team?: string }) => {
  const classes = useStyles();

  return (
    <InfoCard title="ArgoCD ApplicationSet" subheader="Deployed via ArgoCD">
      <Box className={classes.metaRow}>
        {team && <MetaItem label="Team" value={team} />}
        <MetaItem label="Namespace" value={argocd.namespace} />
        <MetaItem label="Project" value={argocd.project} />
      </Box>
      {argocd.repoURL && (
        <Box className={classes.metaRow}>
          <MetaItem label="Source Repo" value={argocd.repoURL} />
        </Box>
      )}
      {argocd.sourcePath && (
        <Box className={classes.metaRow}>
          <MetaItem label="Source Path" value={argocd.sourcePath} />
        </Box>
      )}
    </InfoCard>
  );
};

const ArgoEnvironmentCard = ({ env }: { env: ArgoEnvironmentDetail }) => {
  const classes = useStyles();

  return (
    <InfoCard
      title={
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <Chip
            label={env.name}
            size="small"
            className={classes.envChip}
            style={{ backgroundColor: envColor(env.name) }}
          />
          <Typography variant="h6">Environment</Typography>
        </Box>
      }
    >
      <Box className={classes.metaRow}>
        <MetaItem label="Cluster" value={env.cluster} />
      </Box>
      <Box className={classes.metaRow}>
        <MetaItem label="Server URL" value={env.url} />
      </Box>
      {env.branch && (
        <Box className={classes.metaRow}>
          <MetaItem label="Branch" value={env.branch} />
        </Box>
      )}
    </InfoCard>
  );
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export const ServiceDetailsPage = () => {
  const classes = useStyles();
  const { serviceName } = useParams() as { serviceName: string };
  const [searchParams] = useSearchParams();
  const sourceType = searchParams.get('sourceType') ?? undefined;
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [details, setDetails] = useState<ServiceDetails>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const baseUrl = await discoveryApi.getBaseUrl('gdp-services');
      const qs = sourceType ? `?sourceType=${encodeURIComponent(sourceType)}` : '';
      const response = await fetchApi.fetch(
        `${baseUrl}/services/${serviceName}${qs}`,
      );
      if (!response.ok) {
        if (response.status === 404) throw new Error('Service not found');
        throw new Error(response.statusText);
      }
      setDetails(await response.json());
      setError(undefined);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [discoveryApi, fetchApi, serviceName, sourceType]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <Page themeId="tool">
        <Header title="Loading..." />
        <Content />
      </Page>
    );
  }

  if (error || !details) {
    return (
      <Page themeId="tool">
        <Header title="Service Not Found" />
        <Content>
          <Typography>{error ?? 'Unknown error'}</Typography>
        </Content>
      </Page>
    );
  }

  const isArgo = details.sourceType === 'ArgoCD Application';
  const subtitle = isArgo ? 'ArgoCD ApplicationSet' : 'GDP Service Details';

  return (
    <Page themeId="tool">
      <Header title={details.name} subtitle={subtitle} />
      <Content>
        <Breadcrumbs aria-label="breadcrumb" className={classes.breadcrumb}>
          <Link to="/gdp-services">All Services</Link>
          <Typography>{details.name}</Typography>
        </Breadcrumbs>

        <Grid container spacing={3}>
          {/* ArgoCD details */}
          {isArgo && details.argocd && (
            <>
              <Grid item xs={12}>
                <ArgoOverviewCard argocd={details.argocd} team={details.team} />
              </Grid>
              {details.argocd.environments.map(env => (
                <Grid item xs={12} md={6} key={env.name}>
                  <ArgoEnvironmentCard env={env} />
                </Grid>
              ))}
            </>
          )}

          {/* Service details */}
          {!isArgo && details.vault && details.vault.length > 0 && (
            <Grid item xs={12}>
              <VaultAccessCard paths={details.vault} />
            </Grid>
          )}

          {!isArgo && details.terraform && (
            <Grid item xs={12}>
              <TerraformCard terraform={details.terraform} />
            </Grid>
          )}

          {!isArgo &&
            details.environments.map(env => (
              <Grid item xs={12} key={env.name}>
                <EnvironmentCard env={env} />
              </Grid>
            ))}

          {!isArgo &&
            !details.vault?.length &&
            !details.terraform &&
            details.environments.length === 0 && (
              <Grid item xs={12}>
                <Typography className={classes.noData} variant="h6">
                  No configuration found for this service.
                </Typography>
              </Grid>
            )}
        </Grid>
      </Content>
    </Page>
  );
};
